import { useState } from "react";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { addressIssues } from "@/lib/addressCheck";
import { api, formatError } from "@/lib/api";

const FIELDS = [["line1", "Address line 1", "sm:col-span-2"], ["line2", "Line 2", ""], ["line3", "Line 3", ""], ["city", "Town / City", ""], ["postcode", "Postcode", ""]];

const AddressRow = ({ batchId, w, letter, onChange }) => {
  const a = letter?.recipient_address || {};
  const [form, setForm] = useState({ line1: a.line1 || "", line2: a.line2 || "", line3: a.line3 || "", city: a.city || "", postcode: a.postcode || "" });
  const [busy, setBusy] = useState(false);
  const live = addressIssues(form);
  const call = async (fn, msg) => {
    setBusy(true);
    try { onChange((await fn()).data); toast.success(msg); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <li data-testid={`address-row-${w.row}`} className="py-4 border-b last:border-0 border-amber-200/70 dark:border-amber-900/60">
      <p className="text-xs font-semibold">Row {w.row} · {w.name}</p>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
        {FIELDS.map(([k, label, span]) => (
          <label key={k} className={`space-y-1 ${span}`}>
            <span className="block text-[11px] font-semibold uppercase tracking-wider text-amber-900/80 dark:text-amber-200/80">{label}</span>
            <Input data-testid={`address-row-${k}-${w.row}`} value={form[k]} disabled={!letter}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-9 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100" />
          </label>
        ))}
      </div>
      {live.length > 0 && <ul data-testid={`address-row-issues-${w.row}`} className="mt-2 ml-5 list-disc text-xs space-y-0.5">{live.map((i) => <li key={i}>{i}</li>)}</ul>}
      <div className="mt-3 flex flex-wrap gap-2">
        <button data-testid={`address-row-save-${w.row}`} disabled={busy || !letter} onClick={() => call(() => api.patch(`/bulk/batches/${batchId}/rows/${w.row}/address`, form), "Address updated")}
          className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-500 disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save address</button>
        <button data-testid={`address-row-keep-${w.row}`} disabled={busy} onClick={() => call(() => api.post(`/bulk/batches/${batchId}/rows/${w.row}/address-keep`), "Kept as is")}
          className="h-9 px-4 rounded-full border border-amber-300 text-xs font-semibold hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50">Keep as is</button>
      </div>
    </li>
  );
};

export const BatchAddressFix = ({ batch, letters, onChange }) => (
  <div data-testid="batch-address-warnings" className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-5 text-sm text-amber-800 dark:text-amber-300">
    <p className="font-semibold flex items-center gap-2"><AlertTriangle size={16} /> {batch.address_warnings.length} address{batch.address_warnings.length === 1 ? "" : "es"} may be wrong. Fix them here before you pay.</p>
    <p className="text-xs mt-1">Recipients must type their address to claim, so a mistake can lock them out. There's no need to upload again, and each change is recorded in the letter's history.</p>
    <ul className="mt-1">{batch.address_warnings.map((w) => (
      <AddressRow key={w.row} batchId={batch.id} w={w} letter={letters.find((m) => m.csv_row === w.row)} onChange={onChange} />
    ))}</ul>
  </div>
);
